/* eslint-disable max-lines */
import { useFlags } from 'launchdarkly-react-client-sdk';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { AuthCallback } from 'components/auth/AuthCallback';
import { LoggedOut } from 'components/auth/LoggedOut';
import { RequireAuth } from 'components/auth/RequireAuth';
import { PolicyEdit } from 'pages/clinicalPolicy/PolicyEdit';
import { PolicyList } from 'pages/clinicalPolicy/PolicyList';
import { DiagnosesList } from 'pages/diagnoses/DiagnosesList';
import { DiagnosisDetails } from 'pages/diagnoses/DiagnosisDetails';
import { GoLiveDetails } from 'pages/goLives/GoLiveDetails';
import { GoLives } from 'pages/goLives/GoLives';
import { ProductExpansionDetails } from 'pages/goLives/ProductExpansionDetails';
import { Home } from 'pages/Home';
import { AnnouncementDetails } from 'pages/homeScreenMessaging/AnnouncementDetails';
import { AnnouncementList } from 'pages/homeScreenMessaging/AnnouncementList';
import { CarouselDetails } from 'pages/homeScreenMessaging/CarouselDetails';
import { CarouselList } from 'pages/homeScreenMessaging/CarouselList';
import { HomeScreenPreview } from 'pages/homeScreenMessaging/HomeScreenPreview';
import { HomeScreenPreviewSelection } from 'pages/homeScreenMessaging/HomeScreenPreviewSelection';
import { UpdateDetails } from 'pages/homeScreenMessaging/UpdateDetails';
import { UpdateList } from 'pages/homeScreenMessaging/UpdateList';
import { UrgentMessageDetails } from 'pages/homeScreenMessaging/UrgentMessageDetails';
import { UrgentMessageList } from 'pages/homeScreenMessaging/UrgentMessageList';
import { ListDetails } from 'pages/listManagement/ListDetails';
import { ListManagement } from 'pages/listManagement/ListManagement';
import { CreateSupplier } from 'pages/npiManagement/CreateSupplier';
import { EditChildSupplier } from 'pages/npiManagement/EditChildSupplier';
import { EditSupplier } from 'pages/npiManagement/EditSupplier';
import { PrescriberDetails } from 'pages/npiManagement/PrescriberDetails';
import { PrescriberList } from 'pages/npiManagement/PrescriberList';
import { SupplierList } from 'pages/npiManagement/SupplierList';
import { ParentProductServiceDetails } from 'pages/parentProductService/ParentProductServiceDetails';
import { ParentProductServiceList } from 'pages/parentProductService/ParentProductServiceList';
import { CAGDetails } from 'pages/payerManagement/CAGDetails';
import { PayerMarketDetails } from 'pages/payerManagement/PayerMarketDetails';
import { PayerMarketList } from 'pages/payerManagement/PayerMarketList';
import { PayerSegmentDetails } from 'pages/payerManagement/PayerSegmentDetails';
import { PayerSegmentList } from 'pages/payerManagement/PayerSegmentList';
import { PriceLists } from 'pages/priceLists/PriceLists';
import { PriceListUpload } from 'pages/priceLists/PriceListUpload';
import { ProductConfigDetails } from 'pages/productConfig/ProductConfigDetails';
import { ProductConfigList } from 'pages/productConfig/ProductConfigList';
import { ProductSegmentConfigBulkUpload } from 'pages/productConfig/ProductSegmentConfigBulkUpload';
import { ProductSegmentConfigDetails } from 'pages/productConfig/ProductSegmentConfigDetails';
import { ProductServiceDetails } from 'pages/productService/ProductServiceDetails';
import { ProductServiceHistory } from 'pages/productService/ProductServiceHistory';
import { ProductServiceList } from 'pages/productService/ProductServiceList';
import { ProgramRuleDetails } from 'pages/programManagement/ProgramRuleDetails';
import { ProgramRules } from 'pages/programManagement/ProgramRules';
import { RateRuleDetails } from 'pages/rateRules/RateRuleDetails';
import { RateRules } from 'pages/rateRules/RateRules';
import { RateRulesBulkUpload } from 'pages/rateRules/RateRulesBulkUpload';
import { BackstopPharmacies } from 'pages/routeManagement/backstopPharmacies/BackstopPharmacies';
import { BackstopPharmaciesBulkUpload } from 'pages/routeManagement/backstopPharmacies/BackstopPharmaciesBulkUpload';
import { BackstopPharmacyDetails } from 'pages/routeManagement/backstopPharmacies/BackstopPharmacyDetails';
import { ContinuityOfCareRouteDetails } from 'pages/routeManagement/ContinuityOfCareRouteDetails';
import { GlobalRouteDetails } from 'pages/routeManagement/GlobalRouteDetails';
import { GlobalRouteList } from 'pages/routeManagement/GlobalRouteList';
import { RouteCodes } from 'pages/routeManagement/RouteCodes';
import { RouteRuleDetails } from 'pages/routeManagement/RouteRuleDetails';
import { RouteRules } from 'pages/routeManagement/RouteRules';
import { TherapyGroupDetails } from 'pages/therapyGroups/TherapyGroupDetails';
import { TherapyGroupList } from 'pages/therapyGroups/TherapyGroupList';
import { CompassUserDetails } from 'pages/userManagement/CompassUserDetails';
import { LighthouseUserDetails } from 'pages/userManagement/LighthouseUserDetails';
import { PayerUserDetails } from 'pages/userManagement/PayerUserDetails';
import { PrescriberUserDetails } from 'pages/userManagement/PrescriberUserDetails';
import { SupplierUserDetails } from 'pages/userManagement/SupplierUserDetails';
import { UserManagement } from 'pages/userManagement/UserManagement';
import { EditValueCase } from 'pages/valueCase/EditValueCase';
import { ValueCaseList } from 'pages/valueCase/ValueCaseList';
import { UserType } from 'services/constants';
import { ListSchema, ListSchemaAppRoutePrefix } from 'services/ListManagementHelpers';

export const App = () => {
    const { showGlobalRoutingConfig, showTherapyGroupListConfig } = useFlags();
    const routes = [
        {
            path: '/home',
            main: <Home />,
        },
        {
            path: '/payer-segment',
            main: <PayerSegmentList />,
        },
        {
            path: '/payer-segment/:segmentId',
            main: <PayerSegmentDetails />,
        },
        {
            path: '/payer-market',
            main: <PayerMarketList />,
        },
        {
            path: '/payer-market/:payerMarketId',
            main: <PayerMarketDetails />,
        },
        {
            path: `/go-lives/:segmentId/:goLiveId/cag/:cagId/:programEffectiveDate?`,
            main: <CAGDetails />,
        },
        {
            path: '/go-lives/:segmentId/product-expansion/:productExpansionId',
            main: <ProductExpansionDetails />,
        },
        {
            path: '/go-lives/:segmentId?',
            main: <GoLives />,
        },
        {
            path: '/go-lives/:segmentId/:goLiveId',
            main: <GoLiveDetails />,
        },
        {
            path: '/diagnoses',
            main: <DiagnosesList />,
        },
        {
            path: '/diagnoses/:diagnosisOid',
            main: <DiagnosisDetails />,
        },
        {
            path: '/product-service',
            main: <ProductServiceList />,
        },
        {
            path: '/product-service/:productServiceOid',
            main: <ProductServiceDetails />,
        },
        {
            path: '/product-service-history',
            main: <ProductServiceHistory />,
        },
        {
            path: '/value-cases',
            main: <ValueCaseList />,
        },
        {
            path: '/value-case/:valueCaseId?',
            main: <EditValueCase />,
        },
        {
            path: '/policy-list/:payerId?',
            main: <PolicyList />,
        },
        {
            path: '/policy-edit/:payerId/:draftId',
            main: <PolicyEdit />,
        },
        {
            path: '/user-management/:userType',
            main: <UserManagement />,
        },
        {
            path: `/user-management/${UserType.SUPPLIER}/:upn`,
            main: <SupplierUserDetails />,
        },
        {
            path: `/user-management/${UserType.PRESCRIBER}/:upn`,
            main: <PrescriberUserDetails />,
        },
        {
            path: `/user-management/${UserType.PAYER}/:upn`,
            main: <PayerUserDetails />,
        },
        {
            path: `/user-management/${UserType.COMPASS}/:upn`,
            main: <CompassUserDetails />,
        },
        {
            path: `/user-management/${UserType.LIGHTHOUSE}/:upn`,
            main: <LighthouseUserDetails />,
        },
        {
            path: '/supplier-list',
            main: <SupplierList />,
        },
        {
            path: '/create-supplier',
            main: <CreateSupplier />,
        },
        {
            path: '/edit-supplier/:npi/:payerId?',
            main: <EditSupplier />,
        },
        {
            path: '/supplier/:npi/child/:childNpi',
            main: <EditChildSupplier />,
        },
        {
            path: '/prescribers',
            main: <PrescriberList />,
        },
        {
            path: '/prescribers/:npi',
            main: <PrescriberDetails />,
        },
        ...Object.values(ListSchema)
            .map((listSchema) => [
                {
                    path: `${ListSchemaAppRoutePrefix[listSchema]}/:listType`,
                    main: <ListManagement listSchema={listSchema} />,
                },
                {
                    path: `${ListSchemaAppRoutePrefix[listSchema]}/:listType/:listId`,
                    main: <ListDetails listSchema={listSchema} />,
                },
            ])
            .flat(),
        {
            path: '/route-management/post-live-referral-assignment/:segmentId?',
            main: <BackstopPharmacies />,
        },
        {
            path: '/route-management/post-live-referral-assignment/:segmentId/:backstopPharmacyId',
            main: <BackstopPharmacyDetails />,
        },
        {
            path: '/route-management/post-live-referral-assignment/bulk-upload',
            main: <BackstopPharmaciesBulkUpload />,
        },
        {
            path: '/route-management/route-codes',
            main: <RouteCodes />,
        },
        ...(showGlobalRoutingConfig
            ? [
                  {
                      path: '/route-management/global-routes',
                      main: <GlobalRouteList />,
                  },
                  {
                      path: '/route-management/global-routes/:globalRouteId',
                      main: <GlobalRouteDetails />,
                  },
              ]
            : []),
        {
            path: '/route-management/route-rules/:segmentId?/:benefit?',
            main: <RouteRules />,
        },
        {
            path: '/route-management/route-rules/:segmentId/:benefit/:routeRuleId',
            main: <RouteRuleDetails />,
        },
        {
            path: '/route-management/route-rules/:segmentId/:benefit/continuity-of-care/:routeRuleId',
            main: <ContinuityOfCareRouteDetails />,
        },
        {
            path: '/program-management/program-rules',
            main: <ProgramRules />,
        },
        {
            path: '/program-management/program-rules/:pharmacySkuId',
            main: <ProgramRuleDetails />,
        },
        {
            path: '/product-config-list',
            main: <ProductConfigList />,
        },
        {
            path: '/edit-product-config/:oid',
            main: <ProductConfigDetails />,
        },
        {
            path: '/product-payer-config/:oid/:segment_id/:benefit',
            main: <ProductSegmentConfigDetails />,
        },
        {
            path: '/product-payer-config/bulk-upload',
            main: <ProductSegmentConfigBulkUpload />,
        },
        {
            path: '/parent-products',
            main: <ParentProductServiceList />,
        },
        {
            path: '/parent-products/:parentProductServiceId',
            main: <ParentProductServiceDetails />,
        },
        {
            path: '/rate-rules',
            main: <RateRules />,
        },
        {
            path: '/rate-rules/:rateRuleId',
            main: <RateRuleDetails />,
        },
        {
            path: '/rate-rules/bulk-upload/:payerId?',
            main: <RateRulesBulkUpload />,
        },
        {
            path: '/price-list',
            main: <PriceLists />,
        },
        {
            path: '/price-list/upload',
            main: <PriceListUpload />,
        },
        ...(showTherapyGroupListConfig
            ? [
                  {
                      path: '/therapy-groups',
                      main: <TherapyGroupList />,
                  },
                  {
                      path: '/therapy-groups/:therapyGroupName',
                      main: <TherapyGroupDetails />,
                  },
              ]
            : []),
        {
            path: '/home-screen-messaging/preview',
            main: <HomeScreenPreviewSelection />,
        },
        {
            path: '/home-screen-messaging/preview/:application',
            main: <HomeScreenPreview />,
        },
        {
            path: '/home-screen-messaging/urgent-messages',
            main: <UrgentMessageList />,
        },
        {
            path: '/home-screen-messaging/urgent-messages/:id',
            main: <UrgentMessageDetails />,
        },
        {
            path: '/home-screen-messaging/announcements',
            main: <AnnouncementList />,
        },
        {
            path: '/home-screen-messaging/announcements/:id',
            main: <AnnouncementDetails />,
        },
        {
            path: '/home-screen-messaging/updates',
            main: <UpdateList />,
        },
        {
            path: '/home-screen-messaging/updates/:id',
            main: <UpdateDetails />,
        },
        {
            path: '/feature-campaign/carousel',
            main: <CarouselList />,
        },
        {
            path: '/feature-campaign/carousel/:id',
            main: <CarouselDetails />,
        },
    ];

    return (
        <BrowserRouter basename="/">
            <Routes>
                <Route path="/callback" element={<AuthCallback />} />
                <Route path="/logged-out" element={<LoggedOut />} />
                <Route element={<RequireAuth />}>
                    {routes.map((route) => (
                        <Route key={route.path} path={route.path} element={route.main} />
                    ))}
                </Route>
                <Route path="*" element={<Navigate replace to="/home" />} />
            </Routes>
        </BrowserRouter>
    );
};
